// vClickOutside.ts
import type { Directive, DirectiveBinding } from 'vue'

interface ClickOutsideElement extends HTMLElement {
  _clickOutside?: {
    handler: (event: MouseEvent) => void
    exclude?: () => HTMLElement[]
  }
}

interface ClickOutsideBinding extends DirectiveBinding {
  value: (event: MouseEvent) => void
  modifiers: {
    exclude?: boolean
    dontCloseOnSelect?: boolean
  }
}

const clickOutside: Directive = {
  mounted(el: ClickOutsideElement, binding: ClickOutsideBinding) {
    const handler = (event: MouseEvent) => {
      const target = event.target as Node

      // Check if click was outside the el and its children
      if (!(el === target || el.contains(target))) {
        // Check for any excluded elements
        if (binding.modifiers.exclude) {
          const excludedElements = el._clickOutside?.exclude?.() || []
          const isExcluded = excludedElements.some(excludedEl =>
            excludedEl === target || excludedEl?.contains(target),
          )
          if (isExcluded) {
            return
          }
        }

        // Always call the handler unless dontCloseOnSelect is true
        if (!binding.modifiers.dontCloseOnSelect) {
          binding.value(event)
        }
      }
    }

    // Save the handler on the element for cleanup
    el._clickOutside = {
      handler,
      exclude: binding.modifiers.exclude
        ? () => [] // Override this when using the directive
        : undefined,
    }

    // Use capture phase to ensure we detect all clicks
    document.addEventListener('click', handler, true)
  },

  beforeUnmount(el: ClickOutsideElement) {
    if (el._clickOutside) {
      document.removeEventListener('click', el._clickOutside.handler, true)
      delete el._clickOutside
    }
  },
}

// Register the directive
export default clickOutside
